import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ThemeProvider } from './contexts/ThemeContext.tsx';
import { initSentry } from './config/sentry';
import { AppErrorBoundary } from './components/errors/AppErrorBoundary';
import { installChunkReloadHandler } from './components/errors/chunkReload';
import App from './App.tsx';
import i18n, { i18nReady } from './i18n/config';
import './index.css';
import './utils/consoleOverride';

// Initialize Sentry
initSentry();

// AL-2004: a tab left open across a deploy reloads once instead of going blank.
installChunkReloadHandler();

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // A number here retries EVERY error — query-core only compares failureCount, it never looks
      // at the status — so a 4xx was repeated 3x per query. Client errors don't change on a retry.
      //
      // This is only the DEFAULT: ~175 `retry:` overrides across ~44 hook files win over it, so a
      // deactivated tenant (AL-1684) still costs 3-4 attempts on most screens. Fixing that means
      // sweeping the hooks, which is its own ticket — this narrows the blast radius, it does not
      // close it.
      //
      // 408 and 429 are excluded: they are the two client errors a retry genuinely resolves, and
      // 429 is normally paired with backoff. Network errors, timeouts and 5xx keep the old
      // behaviour.
      retry: (failureCount, error) => {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        const worthRetrying = status === 408 || status === 429;

        if (status !== undefined && status >= 400 && status < 500 && !worthRetrying) {
          return false;
        }

        return failureCount < 2;
      },
      staleTime: Number(import.meta.env.VITE_QUERY_STALE_TIME) || 300000, // Default: 5 minutes
      gcTime: Number(import.meta.env.VITE_QUERY_GC_TIME) || 300000, // Default: 5 minutes
    },
  },
});

/**
 * AL-1778: the translations are fetched now, not bundled, so the first render
 * waits for the active language to be in memory. Painting before it arrives
 * shows a screen of raw keys that then flips to text — worse than the
 * kilobytes this saves.
 *
 * A failure to load must not leave a blank page: i18next answers with the key
 * itself, which is ugly but usable, and the app still boots.
 */
void i18nReady
  .catch((error: unknown) => {
    console.error('[i18n] Failed to preload the active language:', error);
  })
  .finally(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <AppErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
              <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
                <App />
              </ThemeProvider>
            </I18nextProvider>
          </QueryClientProvider>
        </AppErrorBoundary>
      </StrictMode>
    );
  });
